import { useSpeakerFaceMapping } from '@src/hooks/useSpeakerFaceMapping';
import { autoFrameScenesPerSpeaker } from '@src/services/framing/autoFrameScenesPerSpeaker';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { LogManager } from '@src/utility/Logger';
import parseAspectRatio from '@src/utility/parseAspectRatio';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useMemo, useState } from 'react';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { SceneDetection } from '@src/editor/types/sceneDetection';

const logger = LogManager.getLogger('feature:useAutoFrameZoomView');

/**
 * Smart framing defaults to 9:16 when no target is given; every other aspect
 * needs its numeric ratio so the crops size correctly.
 */
function resolveTargetAspectRatio(aspectRatio: string): number | undefined {
  if (aspectRatio === '9:16') return undefined;
  return parseAspectRatio(aspectRatio) ?? undefined;
}

export interface UseAutoFrameZoomViewParams {
  /** Editor whose scenes are replaced and framed. */
  editor: IVideoEditor | undefined;
  /** Output aspect ratio of the surface, e.g. `'9:16'`. */
  aspectRatio: string;
  storyId: string | undefined;
  /** Shot detections in media time; Auto-Frame also splits scenes at each cut. */
  shots?: ReadonlyArray<SceneDetection> | null;
  /**
   * Runs right after the scenes were replaced and framed — the surface pushes
   * them to its own renderer here (scene commands only mark the editor dirty).
   */
  onApplied: () => void;
}

/**
 * Drives the Auto-Frame Zoom View control shared by the Creator Studio Stage
 * reframe modal and the Content Studio clip preview reframe modal: flag gating,
 * the speaker→face data it needs, and the one-click run itself.
 */
export function useAutoFrameZoomView({
  editor,
  aspectRatio,
  storyId,
  shots,
  onApplied,
}: UseAutoFrameZoomViewParams) {
  const userIdentity = useUserIdentity();
  const { contentStudioModalEnableAutoFrameBySpeaker } = useFlagsCombination(
    userIdentity.currentRole,
  );

  // Passing `undefined` keeps the flag-off case from fetching face data at all.
  const { faceDetections, speakerLabelToFaceLabels, isMappingLoading } =
    useSpeakerFaceMapping(
      contentStudioModalEnableAutoFrameBySpeaker ? storyId : undefined,
    );

  // Incremented on each successful run. Surfaces that derive state from
  // `timeline.scenes` include this in their memo deps so they re-read the
  // replaced scenes, and re-derive their slots off the change.
  const [appliedVersion, setAppliedVersion] = useState(0);

  const autoFrameDisabledReason = useMemo<string | null>(() => {
    if (isMappingLoading) return 'Loading face data…';
    if (!faceDetections || faceDetections.length === 0) {
      return 'No face detections for this story';
    }
    return null;
  }, [isMappingLoading, faceDetections]);

  const handleAutoFrameZoomView = useCallback(() => {
    if (!editor) return;

    const result = autoFrameScenesPerSpeaker({
      editor,
      faceDetections: faceDetections ?? [],
      speakerLabelToFaceLabels,
      shots,
      targetAspectRatio: resolveTargetAspectRatio(aspectRatio),
    });

    if (!result.ok) {
      logger.warn('Auto-Frame Zoom View did not run', {
        code: result.error.code,
        message: result.error.message,
      });
      return;
    }

    onApplied();
    setAppliedVersion(version => version + 1);

    logger.info('Auto-Frame Zoom View applied', {
      aspectRatio,
      ...result.value,
    });
  }, [
    editor,
    aspectRatio,
    faceDetections,
    speakerLabelToFaceLabels,
    shots,
    onApplied,
  ]);

  return {
    showAutoFrameZoomView: contentStudioModalEnableAutoFrameBySpeaker,
    autoFrameDisabledReason,
    handleAutoFrameZoomView,
    appliedVersion,
  };
}
